import React, { useContext, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import { NotificationType, SERVER_URL } from "../../../../constants/global";
import { useAuth } from "../../../../hooks/useAuth";
import { ToasterContext } from "../../../shared/Toaster/ToasterContext";
import { useMutationUpdatePharmacy } from "../../../../queries/pharmacy/hooks/useMutationUpdatePharmacy";
import { CircularProgress } from "@mui/material";
import { useQueryPharmacyOptions } from "../../../../queries/pharmacy/hooks/useQueryPharmacyOptions";

interface PharmacyInfoProps {
  pharmacy: any;
  status: string;
}

const PharmacyInfo: React.FunctionComponent<PharmacyInfoProps> = ({
  pharmacy,
  status
}) => {
  const { addToast } = useContext(ToasterContext);
  const { token } = useAuth();
  const [deaLicenseFile, setDeaLicenseFile] = useState<any>(null);
  const [ncpdpLicenseFile, setNcpdpLicenseFile] = useState<any>(null);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [deaLicenseError, setDeaLicenseError] = useState<string>('');
  const [ncpdpLicenseError, setNcpdpLicenseError] = useState<string>('');

  const { data: pharmacyOptionData, loading: pharmacyOptionsLoading } = useQueryPharmacyOptions({});

  const [updatePharmacy, { loading }] = useMutationUpdatePharmacy();
  const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

  // Sanitize filenames to avoid issues with special characters (e.g., '#') during upload
  const sanitizeFilename = (name: string) => {
    return name.replace(/[^a-zA-Z0-9._-]/g, '_');
  }

  const validateFileSize = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE) {
      addToast({
        type: NotificationType.Error,
        title: `File "${file.name}" is too large. Maximum size is 15 MB.`
      });
      return false;
    }
    return true;
  };

  const handleDeaLicenseFileChange = (event: any) => {
    const { files } = event.target;
    if (!files[0]) return;

    if (!validateFileSize(files[0])) {
      event.target.value = ''; // Clear the file input
      return;
    }

    const newFile = new File([files[0]], `${Date.now()}_${sanitizeFilename(files[0].name)}`, {
      type: files[0].type,
    });

    setDeaLicenseFile(newFile);
    setDeaLicenseError(''); // Clear error when file is uploaded
  }
  const handleNcpdpLicenseFileChange = (event: any) => {
    const { files } = event.target;
    if (!files[0]) return;

    if (!validateFileSize(files[0])) {
      event.target.value = ''; // Clear the file input
      return;
    }

    const newFile = new File([files[0]], `${Date.now()}_${sanitizeFilename(files[0].name)}`, {
      type: files[0].type,
    });

    setNcpdpLicenseFile(newFile);
    setNcpdpLicenseError(''); // Clear error when file is uploaded
  }

  const handleSave = async () => {
    // Validate that both files are uploaded
    let hasError = false;
    
    if (!deaLicenseFile) {
      setDeaLicenseError('DEA License is required');
      hasError = true;
    } else {
      setDeaLicenseError('');
    }
    
    if (!ncpdpLicenseFile) {
      setNcpdpLicenseError('Pharmacy License is required');
      hasError = true;
    } else {
      setNcpdpLicenseError('');
    }
    
    if (hasError) {
      return;
    }

    let pharmacyDeaLicenseFileId: number = 0;
    let pharmacyNcpdpLicenseFileId: number = 0;

    pharmacy.PharmacyFiles.forEach((pharmacyFile: any) => {
      if (pharmacyFile.File.type === 'deaLicense') {
        pharmacyDeaLicenseFileId = pharmacyFile.id;
      }
      if (pharmacyFile.File.type === 'ncpdpLicense') {
        pharmacyNcpdpLicenseFileId = pharmacyFile.id;
      }
    });

    try {
      setUploadLoading(true);
      const deaLicenseFileData = new FormData();
      deaLicenseFileData.append('file', deaLicenseFile);
      deaLicenseFileData.append('fileType', 'deaLicense');

      const ncpdpLicenseFileData = new FormData();
      ncpdpLicenseFileData.append('file', ncpdpLicenseFile);
      ncpdpLicenseFileData.append('fileType', 'ncpdpLicense');

      const deaLicensePromise: any = await fetch(`${SERVER_URL}/file/upload`, {
        method: 'POST',
        body: deaLicenseFileData,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const ncpdpLicensePromise: any = await fetch(`${SERVER_URL}/file/upload`, {
        method: 'POST',
        body: ncpdpLicenseFileData,
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (deaLicensePromise.ok && ncpdpLicensePromise.ok) {
        setUploadLoading(true);
        const deaLicenseDataRes = await deaLicensePromise.json();
        const ncpdpLicenseDataRes = await ncpdpLicensePromise.json();

        const deaLicenseRequestData = {
          PharmacyFiles: {
            update: {
              data: {
                File: {
                  connect: {
                    id: deaLicenseDataRes.id
                  }
                }
              },
              where: {
                id: pharmacyDeaLicenseFileId
              }
            }
          }
        }

        const ncpdpLicenseRequestData = {
          PharmacyStatus: {
            update: {
              Status: {
                connect: {
                  id: pharmacyOptionData.findManyStatus.find((status: any) => status.key === 'PENDING').id
                }
              }
            }
          },
          PharmacyFiles: {
            update: {
              data: {
                File: {
                  connect: {
                    id: ncpdpLicenseDataRes.id
                  }
                }
              },
              where: {
                id: pharmacyNcpdpLicenseFileId
              }
            }
          }
        }

        updatePharmacy({ variables: { data: deaLicenseRequestData, where: { id: pharmacy.id } } }).then(() => {
          updatePharmacy({ variables: { data: ncpdpLicenseRequestData, where: { id: pharmacy.id } } }).then(() => {
            window.location.reload();
          }).catch((error: any) => {
            addToast({ type: NotificationType.Error, title: error.message });
          });
        }).catch((error: any) => {
          addToast({ type: NotificationType.Error, title: error.message });
        });
      }
    } catch (error: any) {
      setUploadLoading(true);
      addToast({ type: NotificationType.Error, title: error.message });
    }
  }

  if (pharmacyOptionsLoading) {
    return <CircularProgress sx={{ position: 'absolute', left: 'calc(150px + 50%)', top: '50%' }} color="inherit" />
  }

  return (
    <Box sx={{ width: '100%', height: '100%', mt: 4, textAlign: 'center' }}>
      <Typography
        component="h1"
        variant="h4"
        color="inherit"
        sx={{ mb: 2, fontWeight: 600 }}
      >
        {status === 'PENDING' && 'Congratulations!'}
        {status === 'REJECTED' && 'Sorry!'}
        {status === 'EXPIRED' && 'Sorry!'}
      </Typography>
      <Typography component="h1" variant="h6" color="inherit" sx={{ mt: 2 }}>
        {status === 'PENDING' && (
          <>
            <div>You have successfully created your pharmacy</div>
            <div>We will review your licenses and get back to you.</div>
          </>
        )}
        {status === 'REJECTED' && 'We regret to inform you that your pharmacy registration has been rejected. To further discuss this matter, please contact our administrator at support@tradenetrx.com.'}
        {status === 'EXPIRED' && 'We regret to inform you that your license has expired. To continue using our services, please upload your new licenses below.'}
      </Typography>
      {status === 'EXPIRED' && (
        <>
          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'start', justifyContent: 'center', mt: 4 }}>
            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2 }}>
              <Button size='large' variant="contained" component="label" sx={{ width: '300px' }}>
                Upload DEA License
                <input hidden accept="image/jpeg,image/jpg,image/png,.pdf" multiple type="file" onChange={handleDeaLicenseFileChange} />
              </Button>
              {deaLicenseFile && (
                <Grid sx={{ width: '300px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', mt: 2 }}>{deaLicenseFile.name}</Grid>
              )}
              {deaLicenseError && (
                <Grid sx={{ width: '300px', color: 'error.main', fontSize: '0.875rem', mt: 1 }}>{deaLicenseError}</Grid>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', ml: 2 }}>
              <Button size='large' variant="contained" component="label" sx={{ width: '300px' }}>
                Upload Pharmacy License
                <input hidden accept="image/jpeg,image/jpg,image/png,.pdf" multiple type="file" onChange={handleNcpdpLicenseFileChange} />
              </Button>
              {ncpdpLicenseFile && (
                <Grid sx={{ width: '300px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', mt: 2 }}>{ncpdpLicenseFile.name}</Grid>
              )}
              {ncpdpLicenseError && (
                <Grid sx={{ width: '300px', color: 'error.main', fontSize: '0.875rem', mt: 1 }}>{ncpdpLicenseError}</Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <Button size='large' variant="outlined" sx={{ width: '300px' }} onClick={handleSave}>
              Save
              {(loading || uploadLoading) && <CircularProgress sx={{ ml: 2 }} size={14} color='inherit' />}
            </Button>
          </Grid>
        </>
      )}
    </Box>
  )
}

export default PharmacyInfo;